import type { JSX } from 'react'

// Mirrors the backend ScheduleSchema in
// apps/api/src/routes/conversations/admin/business-hours.ts:
//   Record<Day, { open: 'HH:mm'; close: 'HH:mm'; enabled: boolean }>
export type Day =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday'

export interface DaySchedule {
  open: string
  close: string
  enabled: boolean
}

export type Schedule = Record<Day, DaySchedule>

export const DAYS: Day[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
]

const LABELS: Record<Day, string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
}

/** A fresh all-closed schedule (stable factory, not a shared reference). */
export function emptySchedule(): Schedule {
  return DAYS.reduce((acc, d) => {
    acc[d] = { open: '09:00', close: '17:00', enabled: false }
    return acc
  }, {} as Schedule)
}

export interface BusinessHoursEditorProps {
  value: Schedule
  onChange: (next: Schedule) => void
}

function applyQuickSet(kind: 'business' | 'twentyFourSeven' | 'clear'): Schedule {
  const base = emptySchedule()
  if (kind === 'clear') return base
  for (const d of DAYS) {
    if (kind === 'business') {
      const weekday = d !== 'saturday' && d !== 'sunday'
      base[d] = { open: '09:00', close: '17:00', enabled: weekday }
    } else {
      base[d] = { open: '00:00', close: '23:59', enabled: true }
    }
  }
  return base
}

export default function BusinessHoursEditor({
  value,
  onChange,
}: BusinessHoursEditorProps): JSX.Element {
  function patchDay(day: Day, patch: Partial<DaySchedule>): void {
    onChange({ ...value, [day]: { ...value[day], ...patch } })
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="rounded border border-gray-300 px-2 py-1 text-xs"
          onClick={() => onChange(applyQuickSet('business'))}
        >
          Business hours (Mon–Fri 9–5)
        </button>
        <button
          type="button"
          className="rounded border border-gray-300 px-2 py-1 text-xs"
          onClick={() => onChange(applyQuickSet('twentyFourSeven'))}
        >
          24/7
        </button>
        <button
          type="button"
          className="rounded border border-gray-300 px-2 py-1 text-xs"
          onClick={() => onChange(applyQuickSet('clear'))}
        >
          Clear
        </button>
      </div>

      <div className="flex flex-col gap-2">
        {DAYS.map((day) => {
          const ds = value[day]
          const invalid = ds.enabled && ds.open >= ds.close
          return (
            <div key={day} className="flex flex-col gap-1">
              <div className="flex items-center gap-3">
                <label className="flex w-32 items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    aria-label={`Toggle ${LABELS[day]}`}
                    checked={ds.enabled}
                    onChange={(e) => patchDay(day, { enabled: e.target.checked })}
                  />
                  {LABELS[day]}
                </label>
                <input
                  type="time"
                  aria-label={`${LABELS[day]} open`}
                  disabled={!ds.enabled}
                  value={ds.open}
                  onChange={(e) => patchDay(day, { open: e.target.value })}
                  className="rounded border border-gray-300 px-2 py-1 text-sm"
                />
                <span className="text-gray-400">–</span>
                <input
                  type="time"
                  aria-label={`${LABELS[day]} close`}
                  disabled={!ds.enabled}
                  value={ds.close}
                  onChange={(e) => patchDay(day, { close: e.target.value })}
                  className="rounded border border-gray-300 px-2 py-1 text-sm"
                />
              </div>
              {invalid && (
                <small role="alert" className="text-red-600">
                  {LABELS[day]}: open time must be before close time
                </small>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
